import { createSha256Hex } from './puppyPackageUtils'
import { decodePuppyTopologyRuntimeDocument } from './topologyRuntimeBinaryCodec'
import { readImportedTopologyFaceTriangleRangeBindings } from '../../imported-model/loaders/topology-source/internal'
import { parsePuppyAssemblyDocument } from './parsePuppyAssemblyDocument'
import { parsePuppyProjectModelDocument } from './parsePuppyProjectModelDocument'
import { mapWithConcurrency } from './mapWithConcurrency'
import { normalizePuppyAssemblyDuplicateMates } from './normalizePuppyAssemblyDuplicateMates'
import { validatePuppyPackageGraph, validatePuppyProjectModelGraph } from './validatePuppyPackageGraph'
import { remapPuppyAssemblyMateDocumentNodeReferences } from './remapPuppyAssemblyMateDocument'
import {
  createPuppyContentPackageId,
  createPuppyPackageContentDigest,
} from './puppyPackageCanonicalContent'
import type {
  PuppyBrepIndexDocument,
  PuppyBrepIndexEntry,
  PuppyDisplayNodeBinding,
  PuppyDisplayNodeBodyBinding,
  PuppyDisplayPrimitiveFaceRange,
  PuppyAssemblyDocument,
  PuppyPackageDocumentType,
  PuppyPackageManifest,
  PuppyPackageParseResult,
  PuppyParsedBrepFile,
  PuppyProjectModelDocument,
} from './types'
import {
  PUPPY_ASSEMBLY_PATH,
  PUPPY_BREP_ASCII_ENCODING,
  PUPPY_BREP_INDEX_PATH,
  PUPPY_DISPLAY_GLB_PATH,
  PUPPY_CONTAINER_VERSION,
  PUPPY_LEGACY_CONTAINER_VERSION,
  PUPPY_PROJECT_MODEL_CONTAINER_VERSION,
  PUPPY_MANIFEST_PATH,
  PUPPY_PACKAGE_FORMAT,
  PUPPY_PACKAGE_VERSION,
  PUPPY_PROJECT_MODEL_PATH,
  PUPPY_TOPOLOGY_RUNTIME_ENCODING,
  PUPPY_TOPOLOGY_RUNTIME_PATH,
} from './types'

/**
 * `.puppy` 内部文本解码器。
 */
const puppyTextDecoder = new TextDecoder()

/**
 * BRep payload 摘要校验并发数。
 *
 * 设计说明：
 * 摘要计算和文本解码都发生在导入解析阶段。固定小并发可以避免大装配离线包一次性
 * 创建过多 Promise 和中间字符串，同时不改变解析结果顺序。
 */
const PUPPY_BREP_FILE_PARSE_CONCURRENCY = 4

/**
 * 将 JSON 文件解析为对象。
 *
 * @param bytes JSON 文件字节。
 * @param path 文件路径，用于错误提示。
 * @returns 解析后的对象。
 */
function parseJsonFile(bytes: Uint8Array, path: string): unknown {
  try {
    return JSON.parse(puppyTextDecoder.decode(bytes))
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(`PuppyCAD 离线包中的 ${path} 不是有效 JSON: ${message}`)
  }
}

/**
 * 从 ZIP 文件表读取必需文件。
 *
 * @param files ZIP 文件表。
 * @param path 文件路径。
 * @returns 文件字节。
 */
function readRequiredFile(files: Record<string, Uint8Array>, path: string): Uint8Array {
  const file = files[path]
  if (!file) {
    throw new Error(`PuppyCAD 离线包缺少必需文件: ${path}`)
  }
  return file
}

/**
 * 从 ZIP 文件表中解析 `.puppy` 装配语义文档。
 *
 * @param files 已解压的 ZIP 文件表。
 * @returns `.puppy` 包内的装配语义文档。
 */
function parsePuppyAssemblyDocumentFile(
  files: Record<string, Uint8Array>,
): PuppyAssemblyDocument {
  // assembly.json 是装配层身份的唯一权威来源，完整解析和轻量解析必须共享同一套校验。
  return parsePuppyAssemblyDocument(parseJsonFile(
    readRequiredFile(files, PUPPY_ASSEMBLY_PATH),
    PUPPY_ASSEMBLY_PATH,
  ))
}

/**
 * 规范化解析出的 Mate 拓扑实体 occurrence。
 *
 * 设计说明：
 * 历史 `.puppy` 包可能已经在 assembly node / display binding / topology-runtime 中写入了正确
 * occurrence，但 Mate 文档里的 `topologyRef.bodyBindingId` 仍保留包内局部 occurrence。解析阶段必须先
 * 让 Mate 引用跟随 `topologyRef.assemblyNodeId` 重定位，再交给图校验做严格存在性和归属检查；否则旧包会在
 * 自愈前被拒绝加载，无法进入继续编辑再保存的链路。
 *
 * @param assembly 原始装配语义文档。
 * @returns Mate 拓扑引用已按自身装配节点归一化的装配文档。
 */
function normalizeParsedAssemblyMateTopologyOccurrences(
  assembly: PuppyAssemblyDocument,
): PuppyAssemblyDocument {
  const remappedAssembly = {
    ...assembly,
    mates: remapPuppyAssemblyMateDocumentNodeReferences({
      mates: assembly.mates,
      aliasByNodeId: {},
    }),
  }
  return normalizePuppyAssemblyDuplicateMates(remappedAssembly)
}

/**
 * 判断未知值是否为普通对象。
 *
 * @param value 未知值。
 * @returns 是否为 record。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object')
}

/**
 * 校验非空字符串字段。
 *
 * @param value 待校验对象。
 * @param key 字段名。
 */
function assertNonEmptyString(value: Record<string, unknown>, key: string): void {
  if (typeof value[key] !== 'string' || value[key].trim().length === 0) {
    throw new Error(`PuppyCAD 离线包字段缺失或无效: ${key}`)
  }
}

/**
 * 校验字符串数组字段。
 *
 * @param value 待校验对象。
 * @param key 字段名。
 * @returns 字符串数组。
 */
function assertStringArray(value: Record<string, unknown>, key: string): string[] {
  const raw = value[key]
  if (!Array.isArray(raw) || raw.some((item) => typeof item !== 'string' || item.trim().length === 0)) {
    throw new Error(`PuppyCAD 离线包字段必须是非空字符串数组: ${key}`)
  }
  return raw.map((item) => item.trim())
}

/**
 * 校验数字数组字段。
 *
 * @param value 待校验对象。
 * @param key 字段名。
 * @returns 数字数组。
 */
function assertNumberArray(value: Record<string, unknown>, key: string): number[] {
  const raw = value[key]
  if (!Array.isArray(raw) || raw.some((item) => !Number.isInteger(item) || item < 0)) {
    throw new Error(`PuppyCAD 离线包字段必须是非负整数数组: ${key}`)
  }
  return raw as number[]
}

/**
 * 判断未知值是否为 `.puppy` 支持的 CAD 文档类型。
 *
 * @param value 待校验值。
 * @returns 是否为合法文档类型。
 */
function isPuppyPackageDocumentType(value: unknown): value is PuppyPackageDocumentType {
  return value === 'part' || value === 'assembly'
}

/**
 * 校验并读取 manifest。
 *
 * @param value 未知 JSON。
 * @returns manifest。
 */
function assertPuppyManifest(value: unknown): PuppyPackageManifest {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD 离线包 manifest 必须是对象')
  }

  // v4/v5 只读兼容计划于 2026-08-11 移除；届时版本准入只保留 PUPPY_CONTAINER_VERSION。
  const isSupportedContainerVersion = value.version === PUPPY_CONTAINER_VERSION
    || value.version === PUPPY_PROJECT_MODEL_CONTAINER_VERSION
    || value.version === PUPPY_LEGACY_CONTAINER_VERSION
  if (value.format !== PUPPY_PACKAGE_FORMAT || !isSupportedContainerVersion) {
    throw new Error(`不支持的 PuppyCAD 离线包格式或版本: ${String(value.format)}@${String(value.version)}`)
  }

  if (!isPuppyPackageDocumentType(value.documentType)) {
    throw new Error('PuppyCAD 离线包 manifest 缺少有效 documentType')
  }

  if (
    (
      value.version === PUPPY_CONTAINER_VERSION
      // v5 装配包兼容计划于 2026-08-11 移除；v6 仍必须声明 project-model.json。
      || value.version === PUPPY_PROJECT_MODEL_CONTAINER_VERSION
    )
    && value.documentType === 'assembly'
    && value.projectModelFile !== PUPPY_PROJECT_MODEL_PATH
  ) {
    throw new Error('PuppyCAD v5/v6 装配离线包 manifest 缺少 project-model.json 路径')
  }
  if (
    value.projectModelFile !== undefined
    && value.projectModelFile !== PUPPY_PROJECT_MODEL_PATH
  ) {
    throw new Error('PuppyCAD 离线包 manifest 的 projectModelFile 路径无效')
  }
  if (
    value.projectModelFile !== undefined
    && (
      (
        value.version !== PUPPY_CONTAINER_VERSION
        // v5 project-model.json 兼容计划于 2026-08-11 移除。
        && value.version !== PUPPY_PROJECT_MODEL_CONTAINER_VERSION
      )
      || value.documentType !== 'assembly'
    )
  ) {
    throw new Error('project-model.json 只能声明在 PuppyCAD v5/v6 装配离线包中')
  }

  if (value.version === PUPPY_CONTAINER_VERSION) {
    assertNonEmptyString(value, 'contentDigest')
    assertNonEmptyString(value, 'packageId')
    const contentDigest = (value.contentDigest as string).trim().toLowerCase()
    if (!/^[a-f0-9]{64}$/.test(contentDigest)) {
      throw new Error('PuppyCAD v6 manifest.contentDigest 必须是 SHA-256 十六进制摘要')
    }
    if (value.packageId !== createPuppyContentPackageId(contentDigest)) {
      throw new Error('PuppyCAD v6 manifest.packageId 与 contentDigest 不一致')
    }
  }

  if (
    value.displayFile !== PUPPY_DISPLAY_GLB_PATH
    || value.brepIndexFile !== PUPPY_BREP_INDEX_PATH
    || value.assemblyFile !== PUPPY_ASSEMBLY_PATH
    || value.topologyRuntimeFile !== PUPPY_TOPOLOGY_RUNTIME_PATH
    || value.topologyRuntimeEncoding !== PUPPY_TOPOLOGY_RUNTIME_ENCODING
  ) {
    throw new Error('PuppyCAD 离线包 manifest 内部路径与当前解析器不匹配')
  }

  return value as unknown as PuppyPackageManifest
}

/**
 * 校验 v6 manifest 声明的业务文件内容摘要。
 *
 * @param files 已解压内部文件表。
 * @param manifest 已通过字段校验的 manifest。
 */
async function assertPuppyPackageContentDigest(
  files: Readonly<Record<string, Uint8Array>>,
  manifest: PuppyPackageManifest,
): Promise<void> {
  if (manifest.version !== PUPPY_CONTAINER_VERSION) {
    return
  }
  const businessFiles = Object.fromEntries(
    Object.entries(files).filter(([path]) => path !== PUPPY_MANIFEST_PATH),
  )
  const actualDigest = await createPuppyPackageContentDigest(businessFiles)
  if (actualDigest !== manifest.contentDigest) {
    throw new Error(
      `PuppyCAD v6 离线包内容摘要不匹配: expected=${manifest.contentDigest}, actual=${actualDigest}`,
    )
  }
}

/**
 * 校验 primitive faceIndex 范围。
 *
 * @param value 未知 JSON。
 * @returns primitive faceIndex 范围。
 */
function assertPrimitiveFaceRange(value: unknown): PuppyDisplayPrimitiveFaceRange {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD display node primitiveFaceRanges 条目必须是对象')
  }

  assertNonEmptyString(value, 'primitiveId')
  if (!Number.isInteger(value.startFaceIndex) || (value.startFaceIndex as number) < 0) {
    throw new Error('PuppyCAD primitiveFaceRanges.startFaceIndex 必须是非负整数')
  }
  if (!Number.isInteger(value.endFaceIndex) || (value.endFaceIndex as number) < (value.startFaceIndex as number)) {
    throw new Error('PuppyCAD primitiveFaceRanges.endFaceIndex 必须大于等于 startFaceIndex')
  }

  return value as unknown as PuppyDisplayPrimitiveFaceRange
}

/**
 * 校验 display node 的 body 级绑定。
 *
 * @param value 未知 JSON。
 * @returns body 级绑定。
 */
function assertDisplayNodeBodyBinding(value: unknown): PuppyDisplayNodeBodyBinding {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD display node bodyBindings 条目必须是对象')
  }

  const requiredBindingStringKeys = ['geometryObjectId', 'bodyBindingId', 'bodyId']
  requiredBindingStringKeys.forEach((key) => assertNonEmptyString(value, key))
  const primitiveIds = assertStringArray(value, 'primitiveIds')
  const primitiveIndices = assertNumberArray(value, 'primitiveIndices')
  if (primitiveIds.length !== primitiveIndices.length) {
    throw new Error('PuppyCAD bodyBindings primitiveIds 与 primitiveIndices 数量必须一致')
  }

  const rawRanges = value.primitiveFaceRanges
  if (!Array.isArray(rawRanges) || rawRanges.length === 0) {
    throw new Error('PuppyCAD bodyBindings 缺少 primitiveFaceRanges')
  }
  const primitiveFaceRanges = rawRanges.map(assertPrimitiveFaceRange)
  const primitiveIdSet = new Set(primitiveIds)
  if (primitiveFaceRanges.some((range) => !primitiveIdSet.has(range.primitiveId))) {
    throw new Error('PuppyCAD primitiveFaceRanges 包含不属于当前 body 的 primitiveId')
  }

  const topologyFaceTriangleRanges = value.topologyFaceTriangleRanges === undefined
    ? undefined
    : readImportedTopologyFaceTriangleRangeBindings(value.topologyFaceTriangleRanges)
  if (topologyFaceTriangleRanges && (!Array.isArray(value.topologyFaceTriangleRanges)
    || topologyFaceTriangleRanges.length !== value.topologyFaceTriangleRanges.length)) {
    throw new Error('PuppyCAD bodyBindings.topologyFaceTriangleRanges 包含无效映射')
  }

  return {
    geometryObjectId: value.geometryObjectId as string,
    bodyBindingId: value.bodyBindingId as string,
    bodyId: value.bodyId as string,
    name: typeof value.name === 'string' && value.name.trim().length > 0 ? value.name.trim() : undefined,
    primitiveIds,
    primitiveIndices,
    primitiveFaceRanges,
    ...(topologyFaceTriangleRanges === undefined ? {} : { topologyFaceTriangleRanges }),
  }
}

/**
 * 校验 display node 绑定。
 *
 * @param value 未知 JSON。
 * @returns display node 绑定。
 */
function assertDisplayNodeBinding(value: unknown): PuppyDisplayNodeBinding {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD displayNodes 条目必须是对象')
  }

  assertNonEmptyString(value, 'displayNodeId')
  assertNonEmptyString(value, 'nodeName')
  const geometryObjectIds = assertStringArray(value, 'geometryObjectIds')
  if (!Array.isArray(value.bodyBindings) || value.bodyBindings.length === 0) {
    throw new Error('PuppyCAD displayNodes 缺少 bodyBindings，旧版 .puppy 不再兼容')
  }

  const bodyBindings = value.bodyBindings.map(assertDisplayNodeBodyBinding)
  const bindingObjectIds = new Set(bodyBindings.map((binding) => binding.geometryObjectId))
  if (geometryObjectIds.some((objectId) => !bindingObjectIds.has(objectId))) {
    throw new Error('PuppyCAD displayNodes.geometryObjectIds 与 bodyBindings 不一致')
  }

  return {
    displayNodeId: value.displayNodeId as string,
    assemblyNodeId: typeof value.assemblyNodeId === 'string' && value.assemblyNodeId.trim().length > 0
      ? value.assemblyNodeId.trim()
      : undefined,
    nodeName: value.nodeName as string,
    geometryObjectIds,
    bodyBindings,
  }
}

/**
 * 校验单条 BRep 索引。
 *
 * @param value 未知 JSON。
 * @returns BRep 索引条目。
 */
function assertBrepIndexEntry(value: unknown): PuppyBrepIndexEntry {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD BRep 索引条目必须是对象')
  }

  const requiredEntryStringKeys = ['geometryObjectId', 'encoding', 'brepSha256', 'brepPath']
  if (value.shapeScope !== undefined && value.shapeScope !== 'geometry-object') {
    throw new Error(`PuppyCAD BRep Shape 作用域无效: ${String(value.shapeScope)}`)
  }
  requiredEntryStringKeys.forEach((key) => assertNonEmptyString(value, key))

  if (value.encoding !== PUPPY_BREP_ASCII_ENCODING) {
    throw new Error(`PuppyCAD BRep 索引包含暂不支持的编码: ${String(value.encoding)}`)
  }

  if (typeof value.byteLength !== 'number' || value.byteLength < 0) {
    throw new Error('PuppyCAD BRep 索引条目 byteLength 无效')
  }
  if (value.commandId !== undefined) {
    assertNonEmptyString(value, 'commandId')
  }

  return value as unknown as PuppyBrepIndexEntry
}

/**
 * 校验并读取 BRep 索引文档。
 *
 * @param value 未知 JSON。
 * @returns BRep 索引文档。
 */
function assertBrepIndexDocument(value: unknown): PuppyBrepIndexDocument {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD BRep 索引必须是对象')
  }

  if (value.version !== PUPPY_PACKAGE_VERSION) {
    throw new Error(`不支持的 PuppyCAD BRep 索引版本: ${String(value.version)}`)
  }

  if (!Array.isArray(value.displayNodes)) {
    throw new Error('PuppyCAD BRep 索引缺少 displayNodes')
  }

  if (!Array.isArray(value.entries)) {
    throw new Error('PuppyCAD BRep 索引缺少 entries')
  }

  return {
    version: PUPPY_PACKAGE_VERSION,
    displayNodes: value.displayNodes.map(assertDisplayNodeBinding),
    entries: value.entries.map(assertBrepIndexEntry),
  }
}

/**
 * 读取并校验 BRep 文件。
 *
 * @param input 读取输入。
 * @returns BRep 文件。
 */
async function readParsedBrepFile(input: {
  files: Record<string, Uint8Array>
  entry: PuppyBrepIndexEntry
}): Promise<PuppyParsedBrepFile> {
  const bytes = readRequiredFile(input.files, input.entry.brepPath)
  const sha256 = await createSha256Hex(bytes)
  if (sha256 !== input.entry.brepSha256) {
    throw new Error(`PuppyCAD BRep 文件摘要不匹配: ${input.entry.brepPath}`)
  }

  if (bytes.byteLength !== input.entry.byteLength) {
    throw new Error(`PuppyCAD BRep 文件长度不匹配: ${input.entry.brepPath}`)
  }

  return {
    path: input.entry.brepPath,
    bytes,
    payload: puppyTextDecoder.decode(bytes),
  }
}

/**
 * 按 BRep 内容摘要收集需要实际读取的文件条目。
 *
 * 设计说明：
 * 离线包允许多个几何对象或装配实例复用同一个 BRep 文件。导入结果也以 sha 作为
 * `brepFilesBySha256` 的缓存键，因此这里只需要为每个 sha 选择一个可读取文件。
 *
 * @param entries BRep 索引条目。
 * @param files ZIP 文件表。
 * @returns 去重后的 BRep 文件读取条目。
 */
function collectUniqueBrepFileEntries(
  entries: readonly PuppyBrepIndexEntry[],
  files: Readonly<Record<string, Uint8Array>>,
): PuppyBrepIndexEntry[] {
  const entryBySha256 = new Map<string, PuppyBrepIndexEntry>()

  entries.forEach((entry) => {
    const existingEntry = entryBySha256.get(entry.brepSha256)
    if (!existingEntry) {
      entryBySha256.set(entry.brepSha256, entry)
      return
    }

    // 同一 sha 对应的内容长度应保持一致；路径可以不同，因为导出侧可能只保留一个去重文件。
    if (existingEntry.encoding !== entry.encoding || existingEntry.byteLength !== entry.byteLength) {
      throw new Error(`PuppyCAD BRep 索引同一摘要对应不一致文件元数据: ${entry.brepSha256}`)
    }

    // 如果先遇到的条目路径在 ZIP 中不存在，后续条目存在时使用可读取路径恢复。
    if (!files[existingEntry.brepPath] && files[entry.brepPath]) {
      entryBySha256.set(entry.brepSha256, entry)
    }
  })

  return Array.from(entryBySha256.values())
}

/**
 * 轻量解析 PuppyCAD 离线包中的装配语义文档。
 *
 * 设计说明：加载 root `.puppy` 后补齐一层节点时，只需要知道包内 root 的 direct child 身份；
 * 这里不解析 display GLB、BRep 与拓扑运行时，避免为了判重重复承担完整包解析成本。
 *
 * @param bytes `.puppy` 文件字节。
 * @returns 包内 `assembly.json` 的装配语义文档。
 */
export async function parsePuppyPackageAssemblyDocument(bytes: Uint8Array): Promise<PuppyAssemblyDocument> {
  const { unzipSync } = await import('three/examples/jsm/libs/fflate.module.js')
  const files = unzipSync(bytes)

  return normalizeParsedAssemblyMateTopologyOccurrences(parsePuppyAssemblyDocumentFile(files))
}

/**
 * 项目模型加载准备阶段所需的轻量 `.puppy` 源文档。
 */
export interface PuppyProjectModelPackageDocuments {
  /** 包内装配语义文档；v4-v6 均必须存在。 */
  readonly assembly: PuppyAssemblyDocument
  /** 包内可编辑 occurrence 文档；v4 包没有 project-model.json 时为 null。 */
  readonly projectModel: PuppyProjectModelDocument | null
}

/**
 * 从已解压 ZIP 文件表解析项目模型源文档。
 *
 * v4 只校验并返回 assembly.json；存在 project-model.json 时则保持 v5/v6 现有的 manifest、
 * 内容摘要、容器版本、文档类型与项目图严格校验，禁止因为轻量入口而放宽协议。
 *
 * @param files 已解压的 ZIP 文件表。
 * @returns v4 的 assembly + null，或 v5/v6 的 assembly + projectModel。
 */
async function parsePuppyPackageProjectModelDocumentsFromFiles(
  files: Record<string, Uint8Array>,
): Promise<PuppyProjectModelPackageDocuments> {
  const assembly = normalizeParsedAssemblyMateTopologyOccurrences(parsePuppyAssemblyDocumentFile(files))
  const projectModelBytes = files[PUPPY_PROJECT_MODEL_PATH]
  if (!projectModelBytes) {
    return { assembly, projectModel: null }
  }

  const manifest = assertPuppyManifest(parseJsonFile(
    readRequiredFile(files, PUPPY_MANIFEST_PATH),
    PUPPY_MANIFEST_PATH,
  ))
  await assertPuppyPackageContentDigest(files, manifest)
  if (
    (
      manifest.version !== PUPPY_CONTAINER_VERSION
      // v5 项目快照兼容计划于 2026-08-11 移除。
      && manifest.version !== PUPPY_PROJECT_MODEL_CONTAINER_VERSION
    )
    || manifest.documentType !== 'assembly'
    || manifest.projectModelFile !== PUPPY_PROJECT_MODEL_PATH
  ) {
    throw new Error('project-model.json 只能存在于 PuppyCAD v5/v6 装配离线包中')
  }

  const projectModel = parsePuppyProjectModelDocument(parseJsonFile(
    projectModelBytes,
    PUPPY_PROJECT_MODEL_PATH,
  ))
  validatePuppyProjectModelGraph({ assembly, projectModel })
  return { assembly, projectModel }
}

/**
 * 单次解压并轻量解析 `.puppy` 的项目模型源文档。
 *
 * 该入口不读取 display、BRep 或 topology runtime。v4 包返回经过标准规范化的 assembly 与
 * `projectModel: null`，由调用方决定是否执行 legacy migration；v5/v6 保持严格项目协议校验。
 *
 * @param bytes `.puppy` 容器字节。
 * @returns v4 的 assembly + null，或 v5/v6 的 assembly + projectModel。
 */
export async function parsePuppyPackageProjectModelDocuments(
  bytes: Uint8Array,
): Promise<PuppyProjectModelPackageDocuments> {
  const { unzipSync } = await import('three/examples/jsm/libs/fflate.module.js')
  return parsePuppyPackageProjectModelDocumentsFromFiles(unzipSync(bytes))
}

/**
 * 项目模型加载准备阶段所需的轻量 `.puppy` 快照。
 */
export interface PuppyProjectModelPackageSnapshot {
  /** 包内装配语义文档。 */
  readonly assembly: PuppyAssemblyDocument
  /** 包内可编辑项目 occurrence 文档。 */
  readonly projectModel: PuppyProjectModelDocument
}

/**
 * 轻量解析 `.puppy` 中的可编辑项目 occurrence 快照。
 *
 * 该入口只服务项目模型加载计划，不读取 display、BRep 或 topology runtime。v4 包没有
 * `project-model.json` 时返回 null，继续走现有后端树或 legacy 精确迁移路径。
 *
 * @param bytes `.puppy` 容器字节。
 * @returns v5/v6 项目快照；容器没有项目文档时返回 null。
 * @throws 当容器包含 `project-model.json`，但解析结果缺少项目文档时。
 */
export async function parsePuppyPackageProjectModelSnapshot(
  bytes: Uint8Array,
): Promise<PuppyProjectModelPackageSnapshot | null> {
  const { unzipSync } = await import('three/examples/jsm/libs/fflate.module.js')
  const files = unzipSync(bytes)
  const projectModelBytes = files[PUPPY_PROJECT_MODEL_PATH]
  if (!projectModelBytes) {
    return null
  }
  const documents = await parsePuppyPackageProjectModelDocumentsFromFiles(files)
  if (!documents.projectModel) {
    throw new Error('project-model.json 解析结果缺少项目文档')
  }
  return {
    assembly: documents.assembly,
    projectModel: documents.projectModel,
  }
}

/**
 * 解析完整 PuppyCAD 离线包。
 *
 * @param bytes `.puppy` 文件字节。
 * @returns 包含清单、装配、拓扑、显示 GLB 与 BRep 文件的完整解析结果。
 */
export async function parsePuppyPackage(bytes: Uint8Array): Promise<PuppyPackageParseResult> {
  const { unzipSync } = await import('three/examples/jsm/libs/fflate.module.js')
  const files = unzipSync(bytes)
  const manifest = assertPuppyManifest(parseJsonFile(
    readRequiredFile(files, PUPPY_MANIFEST_PATH),
    PUPPY_MANIFEST_PATH,
  ))
  await assertPuppyPackageContentDigest(files, manifest)
  const brepIndex = assertBrepIndexDocument(parseJsonFile(
    readRequiredFile(files, PUPPY_BREP_INDEX_PATH),
    PUPPY_BREP_INDEX_PATH,
  ))
  const assembly = normalizeParsedAssemblyMateTopologyOccurrences(parsePuppyAssemblyDocumentFile(files))
  const projectModel = manifest.projectModelFile
    ? parsePuppyProjectModelDocument(parseJsonFile(
        readRequiredFile(files, manifest.projectModelFile),
        manifest.projectModelFile,
      ))
    : null
  const topologyRuntime = decodePuppyTopologyRuntimeDocument(readRequiredFile(files, PUPPY_TOPOLOGY_RUNTIME_PATH))
  const displayGlbBytes = readRequiredFile(files, PUPPY_DISPLAY_GLB_PATH)
  const uniqueBrepFileEntries = collectUniqueBrepFileEntries(brepIndex.entries, files)
  const parsedBrepFiles = await mapWithConcurrency(
    uniqueBrepFileEntries,
    PUPPY_BREP_FILE_PARSE_CONCURRENCY,
    async (entry) => [
      entry.brepSha256,
      await readParsedBrepFile({ files, entry }),
    ] as const,
  )

  const result: PuppyPackageParseResult = {
    manifest,
    brepIndex,
    assembly,
    projectModel,
    topologyRuntime,
    displayGlbBytes,
    brepFilesBySha256: Object.fromEntries(parsedBrepFiles),
  }
  validatePuppyPackageGraph(result)
  return result
}
